import { ElementRef, Injectable, inject } from '@angular/core';

import { Observable, ReplaySubject, Subject, share } from 'rxjs';

import { RESIZE_OBSERVER } from './resize-observer.token';

@Injectable({ providedIn: 'root' })
export class ResizeObserverService {
    readonly #ResizeObserver = inject(RESIZE_OBSERVER);
    private readonly changes$ = new Subject<ResizeObserverEntry[]>();
    private readonly elementChanges = new WeakMap<HTMLElement, Observable<ResizeObserverEntry>>();
    private readonly observer = new this.#ResizeObserver((entries: ResizeObserverEntry[]) => this.changes$.next(entries));

    observe(element: ElementRef<HTMLElement> | HTMLElement, options?: ResizeObserverOptions): Observable<ResizeObserverEntry> {
        const el = element instanceof ElementRef ? element.nativeElement : element;
        let elementChanges$ = this.elementChanges.get(el);
        if (!elementChanges$) {
            this.observer.observe(el, options);

            elementChanges$ = new Observable<ResizeObserverEntry>((observer) => {
                const inner = this.changes$.subscribe((changes) => {
                    const change = changes.find((e) => e.target === el);
                    if (change) {
                        observer.next(change);
                    }
                });

                return () => {
                    inner.unsubscribe();
                    this.observer.unobserve(el);
                    this.elementChanges.delete(el);
                };
            }).pipe(share({ connector: () => new ReplaySubject(1), resetOnRefCountZero: true }));

            this.elementChanges.set(el, elementChanges$);
        }

        return elementChanges$;
    }
}
